import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { createTypedFormGroup, FormGroupTypeWithFormControls } from "@valg-shared/forms";
import {
  ElectionObservableParams,
  ExportDataForm,
  FVEVFAElections,
} from "./export-data-page.model";
import {
  ElectionClient,
  ElectionType,
  ExportDataClient, FileResponse,
} from "@shared/clients/api.generated";
import { TranslateService } from "@ngx-translate/core";
import { camelToSnakeCase } from "@valg-shared/utils/string-utils";
import { ValgOptionInput } from "@valg-shared/components/input/input.types";
import { SvgIcon } from "@valg-shared/utils/svg-icon";
import { Observable, Subject, takeUntil } from "rxjs";
import { saveFile } from "@valg-shared/utils/file-utils";
import { SpinnerService } from "@valg-shared/services/spinner/spinner.service";
import { ExportFileService } from "@valg-shared/services/export-file/export-file.service";
import { DateTimeService } from "@valg-shared/services/date-time-service/date-time.service";

@Component({
  selector: 'valg-dk-export-data-page',
  templateUrl: './export-data-page.component.html',
  providers: [ SpinnerService ]
})


export class ExportDataPageComponent implements OnInit, OnDestroy {
  form?: FormGroup<FormGroupTypeWithFormControls<ExportDataForm>>;
  SvgIcon = SvgIcon;
  electionType: ValgOptionInput<string>[] = [];
  electionDate: ValgOptionInput<string>[] = [];
  constituencies: ValgOptionInput<string>[] = [];
  regions: ValgOptionInput<string>[] = [];
  municipalitiesFromElection: ValgOptionInput<string>[] = [];
  nominationDistrictsFromConstituency: ValgOptionInput<string>[] = [];
  municipalitiesFromRegion: ValgOptionInput<string>[] = [];
  electionAreas: ValgOptionInput<string>[] = [];
  isElectionTypeSelected? = false;
  isFVEVFAElections? = false;
  isRVElection? = false;
  isKVElection? = false;
  isMunicipalityKVElection? = false;
  isOptionalNominationDistrictFVEVFAElection? = false;
  isOptionalMunicipalityRVElection? = false;
  isOptionalElectionAreas? = false;
  private readonly destroy$ = new Subject<void>();
  public readonly guideFilePath: string =
    "assets/guides/sftp-guide-public/VALG - Vejledning til hent af valgdata (Offentligheden).pdf";

  constructor(
    private readonly fb: FormBuilder,
    private readonly translateService: TranslateService,
    private readonly electionClient: ElectionClient,
    private readonly exportDataClient: ExportDataClient,
    private readonly spinner: SpinnerService,
    private readonly exportFileService: ExportFileService,
    private readonly dateTimeService: DateTimeService
  ) {
  }

  ngOnInit() {
    this.initializeData();
    this.initializeForm();
    this.electionTypeSubscription();
    this.electionDateSubscription();
    this.regionsSubscription();
    this.municipalitiesFromRegionSubscription();
    this.constituenciesSubscription();
    this.municipalitiesFromElectionSubscription();
    this.nominationDistrictsFromConstituencySubscription();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  nominationDistrictsFromConstituencySubscription() {
    this.form?.controls.nominationDistrictsFromConstituency.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(nominationDistrictSelectedValue => {
      if (nominationDistrictSelectedValue) {
        this.isOptionalElectionAreas = true;
        this.exportDataClient.getExportElectionAreas(nominationDistrictSelectedValue, null).subscribe(result => {
          this.electionAreas = result.map(dto => {
            return {
              label: dto.name,
              value: dto.id
            }
          })
        })
      } else {
        this.isOptionalElectionAreas = false;
      }
        this.form?.patchValue({
          electionAreas: undefined,
        })
    })
  }

  municipalitiesFromElectionSubscription() {
    this.form?.controls.municipalitiesFromElection.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(municipalityFromElectionSelectedValue => {
      if (municipalityFromElectionSelectedValue) {
        this.isMunicipalityKVElection = true;
        this.isOptionalElectionAreas = true;
        this.exportDataClient.getExportElectionAreas(null, municipalityFromElectionSelectedValue).subscribe(result => {
          this.electionAreas = result.map(dto => {
            return {
              label: dto.name,
              value: dto.id
            }
          })
        })
      } else {
        this.isMunicipalityKVElection = false;
        this.isOptionalElectionAreas = false;
      }
        this.form?.patchValue({
          electionAreas: undefined,
        })
    })
  }

  constituenciesSubscription() {
    this.form?.controls.constituencies.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(constituencyDtoSelectedValue => {
      if (constituencyDtoSelectedValue) {
        this.isOptionalNominationDistrictFVEVFAElection = true;
        this.exportDataClient.getNominationDistrictListByElection(constituencyDtoSelectedValue).subscribe(result => {
          this.nominationDistrictsFromConstituency = result.map(dto => {
            return {
              label: dto.name,
              value: dto.id
            }
          })
        })
      } else {
        this.isOptionalNominationDistrictFVEVFAElection = false;
        this.isOptionalElectionAreas = false;
      }
      this.form?.patchValue({
        nominationDistrictsFromConstituency: undefined,
        electionAreas: undefined,
      })
    })
  }

  municipalitiesFromRegionSubscription() {
    this.form?.controls.municipalitiesFromRegion.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(municipalityFromRegionSelectedValue => {
      if (municipalityFromRegionSelectedValue) {
        this.isOptionalElectionAreas = true;
        this.exportDataClient.getExportElectionAreas(null, municipalityFromRegionSelectedValue).subscribe(result => {
          this.electionAreas = result.map(dto => {
            return {
              label: dto.name,
              value: dto.id
            }
          })
        })
      } else {
        this.isOptionalElectionAreas = false;
      }
      this.form?.patchValue({
        electionAreas: undefined,
      })
    })
  }

  regionsSubscription() {
    this.form?.controls.regions.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(regionDtoSelectedValue => {
      if (regionDtoSelectedValue && this.form?.controls.electionIdByDate.value) {
        this.isOptionalMunicipalityRVElection = true;
        this.exportDataClient.getExportMunicipalities(this.form?.controls.electionIdByDate.value,
          regionDtoSelectedValue).subscribe(result => {
          this.municipalitiesFromRegion = result.map(dto => {
            return {
              label: dto.name,
              value: dto.id
            }
          })
        })
      } else {
        this.isOptionalMunicipalityRVElection = false;
        this.isOptionalElectionAreas = false;
      }
        this.form?.patchValue({
          municipalitiesFromRegion: undefined,
          electionAreas: undefined,
        })
    })
  }

  electionDateSubscription() {
    this.form?.controls.electionIdByDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(electionDateDtoSelectedValue => {
      if (electionDateDtoSelectedValue && this.form?.controls.electionType.value) {
        const electionType = this.form?.controls.electionType.value;

        if (FVEVFAElections.includes(electionType)) {
          this.handleFVEVFAElections(electionDateDtoSelectedValue);
        } else if (electionType === ElectionType.RegionalElection) {
          this.handleRVElections(electionDateDtoSelectedValue);
        } else if (electionType === ElectionType.MunicipalElection) {
          this.handleKVElections(electionDateDtoSelectedValue);
        }
      }
      this.form?.patchValue({
        constituencies: undefined,
        municipalitiesFromElection: undefined,
        regions: undefined,
      })
    });
  }

  handleFVEVFAElections(electionDateDtoSelectedValue: string) {
    this.isFVEVFAElections = true;
    this.exportDataClient.getConstituencyListByElection(electionDateDtoSelectedValue).subscribe(result => {
      this.constituencies = result.map(dto => {
        return {
          label: dto.name,
          value: dto.id
        }
      });
    });
  }

  handleRVElections(electionDateDtoSelectedValue: string) {
    this.isRVElection = true;
    this.exportDataClient.getRegionListByElection(electionDateDtoSelectedValue).subscribe(result => {
      this.regions = result.map(dto => {
        return {
          label: dto.name,
          value: dto.id
        }
      });
    });
  }

  handleKVElections(electionDateDtoSelectedValue: string) {
    this.isKVElection = true;
    this.exportDataClient.getExportMunicipalities(electionDateDtoSelectedValue, null).subscribe(result => {
      this.municipalitiesFromElection = result.map(dto => {
        return {
          label: dto.name,
          value: dto.id
        }
      });
    });
  }


  electionTypeSubscription() {
    this.form?.controls.electionType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(electionTypeSelectedValue => {
      if (electionTypeSelectedValue) {
        this.isElectionTypeSelected = false;
        this.electionClient.getElectionDatesByElection(electionTypeSelectedValue).subscribe(result => {
          this.electionDate = result.map(dto => {
            return {
              label: this.dateTimeService.convertTimestampToLongDate(dto?.electionDate)  ?? "",
              value: dto?.electionId
            }
          })
          this.isElectionTypeSelected = true;
        })
      }
      this.resetFormState();
    });
  }

  resetFormState() {
    this.form?.patchValue({
      electionIdByDate: undefined,
      constituencies: undefined,
      regions: undefined,
      municipalitiesFromElection: undefined,
      nominationDistrictsFromConstituency: undefined,
      municipalitiesFromRegion: undefined,
      electionAreas: undefined,
    })
    this.isElectionTypeSelected = false;
    this.isFVEVFAElections = false;
    this.isRVElection = false;
    this.isKVElection = false;
    this.isMunicipalityKVElection = false;
    this.isOptionalNominationDistrictFVEVFAElection = false;
    this.isOptionalMunicipalityRVElection = false;
    this.isOptionalElectionAreas = false;
  }

  initializeData() {
    for (const item in ElectionType) {
      this.translateService.get('enums.election_type.' + camelToSnakeCase(item)).subscribe(x => {
        this.electionType.push({ label: x, value: item as ElectionType })
      })
      ;
    }
  }

  initializeForm() {
    this.form = createTypedFormGroup<ExportDataForm>(this.fb, {
      electionType: [  undefined, Validators.required ],
      electionIdByDate: [  undefined, Validators.required ],
      constituencies: [  undefined, Validators.required ],
      regions: [  undefined, Validators.required ],
      municipalitiesFromElection: [  undefined, Validators.required ],
      nominationDistrictsFromConstituency: [  undefined ],
      municipalitiesFromRegion: [  undefined ],
      electionAreas: [ undefined ],
    });
  }

  isExportButtonEnabled(): boolean {
    // Ensure election type is selected
    if (!this.isElectionTypeSelected) return false;

    // If it's a municipal election, ensure a municipality is selected
    if (this.isKVElection && !this.form?.controls.municipalitiesFromElection.value) return false;

    // Check other conditions for enabling the button
    return this.isOptionalNominationDistrictFVEVFAElection! ||
      this.isOptionalMunicipalityRVElection! ||
      this.isMunicipalityKVElection!;
  }

  onDownloadReportClick() {
    const electionIdByDate = this.form?.controls.electionIdByDate.value!;
    const constituencies = this.form?.controls.constituencies.value!;
    const nominationDistrictsFromConstituency = this.form?.controls.nominationDistrictsFromConstituency.value;
    const electionAreas = this.form?.controls.electionAreas.value;
    const municipalitiesFromElection = this.form?.controls.municipalitiesFromElection.value!;
    const regions = this.form?.controls.regions.value!;
    const municipalitiesFromRegion = this.form?.controls.municipalitiesFromRegion.value;
    let selectedElectionType = this.form?.controls.electionType.value;
    const handler = this.getExportDataCsv({
      electionType: selectedElectionType,
      electionIdByDate: electionIdByDate,
      constituencies: constituencies,
      nominationDistrictsFromConstituency: nominationDistrictsFromConstituency,
      electionAreas: electionAreas,
      municipalitiesFromElection: municipalitiesFromElection,
      regions: regions,
      municipalitiesFromRegion: municipalitiesFromRegion
    });
    this.spinner.attachSpinner(handler).subscribe(result => {
      const fileName = this.exportFileService.getReportFileNameWithDate(result.fileName, ".csv");
      saveFile(result.data, fileName);
    });
  }

  private parseDate(label: string): Date {
    const [ day, month, year ] = label.split(' ');
    const formatter = new Intl.DateTimeFormat('da-DK', { month: 'long' });
    const months = Array.from({ length: 12 }, (_, i) =>
      formatter.format(new Date(2000, i, 1)).toLowerCase()
    );
    const monthIndex = months.indexOf(month.toLowerCase());
    if (monthIndex === -1) throw new Error(`Invalid month: ${month}`);
    return new Date(parseInt(year, 10), monthIndex, parseInt(day, 10));
  }

  customElectionDateSort = (a: ValgOptionInput<string>, b: ValgOptionInput<string>): number => {
    // Prioritize the empty option
    if (a.isEmptyOption && !b.isEmptyOption) return -1;
    if (!a.isEmptyOption && b.isEmptyOption) return 1;

    // Handle date sorting
    const dateA = this.parseDate(a.label);
    const dateB = this.parseDate(b.label);

    return dateA.getTime() - dateB.getTime();
  };

  private getExportDataCsv(params: ElectionObservableParams): Observable<FileResponse> {
    switch (params.electionType) {
      case ElectionType.GeneralElection:
        return this.exportDataClient.exportFvDataCsv(params.electionIdByDate, params.constituencies,
          params.nominationDistrictsFromConstituency, params.electionAreas);
      case ElectionType.EuropeanParliamentElection:
        return this.exportDataClient.exportEvDataCsv(params.electionIdByDate, params.constituencies,
          params.nominationDistrictsFromConstituency, params.electionAreas);
      case ElectionType.Referendum:
        return this.exportDataClient.exportFaDataCsv(params.electionIdByDate, params.constituencies,
          params.nominationDistrictsFromConstituency, params.electionAreas);
      case ElectionType.MunicipalElection:
        return this.exportDataClient.exportKvDataCsv(params.electionIdByDate, params.municipalitiesFromElection, params.electionAreas);
      case ElectionType.RegionalElection:
        return this.exportDataClient.exportRvDataCsv(params.electionIdByDate, params.regions,
          params.municipalitiesFromRegion, params.electionAreas);
      default:
        throw new Error('Election type is not found.');
    }
  }
}
